import React from "react";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import { DATE_MODE_OPTIONS } from "../constants";
import type { DateMode } from "../types";

type FiltersBarProps = {
  serverOptions: string[];
  serverFilter: string;
  onServerFilterChange: (value: string) => void;
  branchOptions: string[];
  branchFilter: string[];
  onBranchFilterChange: (value: string[]) => void;
  controlMode: "recetas" | "inventario";
  onControlModeChange: (value: "recetas" | "inventario") => void;
  differenceFilter: "todas" | "con" | "sin" | "positivas" | "negativas";
  onDifferenceFilterChange: (
    value: "todas" | "con" | "sin" | "positivas" | "negativas",
  ) => void;
  metricMode: "cantidad" | "monto" | "ambos";
  onMetricModeChange: (value: "cantidad" | "monto" | "ambos") => void;
  dateModeDropdownOpen: boolean;
  toggleDateModeDropdown: () => void;
  currentModeOption: { label: string; icon: string };
  dateSelectionMode: DateMode;
  onModeChange: (mode: DateMode) => void;
  renderDateModeInput: () => React.ReactNode;
};

const FiltersBar = ({
  serverOptions,
  serverFilter,
  onServerFilterChange,
  branchOptions,
  branchFilter,
  onBranchFilterChange,
  controlMode,
  onControlModeChange,
  differenceFilter,
  onDifferenceFilterChange,
  metricMode,
  onMetricModeChange,
  dateModeDropdownOpen,
  toggleDateModeDropdown,
  currentModeOption,
  dateSelectionMode,
  onModeChange,
  renderDateModeInput,
}: FiltersBarProps) => (
  <div className="d-flex flex-column gap-3 mb-3">
    <div className="w-100">
      <FormGroup className="w-100">
        <Label className="text-muted text-uppercase small fw-semibold">
          Periodo (día / semana / mes / rango)
        </Label>
        <Dropdown
          className="input-group flex-nowrap"
          isOpen={dateModeDropdownOpen}
          toggle={toggleDateModeDropdown}
        >
          <DropdownToggle
            as="button"
            className="btn btn-light border arrow-none d-flex align-items-center gap-2"
          >
            <i className={`${currentModeOption.icon} text-primary`} />
            <span>{currentModeOption.label}</span>
          </DropdownToggle>
          {renderDateModeInput()}
          <DropdownMenu
            as="ul"
            className="list-unstyled w-100 dropdown-menu-list mb-0"
          >
            <SimpleBar style={{ maxHeight: "220px" }} className="px-3">
              {DATE_MODE_OPTIONS.map((option) => (
                <DropdownItem
                  as="li"
                  key={option.key}
                  onClick={() => onModeChange(option.key)}
                  active={option.key === dateSelectionMode}
                  className="dropdown-item d-flex align-items-start gap-2"
                >
                  <div className="flex-shrink-0">
                    <i className={`${option.icon} text-primary`} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">{option.label}</span>
                      {option.key === dateSelectionMode ? (
                        <i className="ri-check-line text-success ms-2" />
                      ) : null}
                    </div>
                    <small className="text-muted">{option.description}</small>
                  </div>
                </DropdownItem>
              ))}
            </SimpleBar>
          </DropdownMenu>
        </Dropdown>
      </FormGroup>
    </div>
    <div className="w-100">
      <FormGroup>
        <Label className="text-muted text-uppercase small fw-semibold">
          Servidor
        </Label>
        <Input
          type="select"
          value={serverFilter}
          onChange={(event) => onServerFilterChange(event.target.value)}
          disabled={serverOptions.length === 0}
        >
          <option value="" disabled>
            {serverOptions.length === 0
              ? "Cargando..."
              : "Selecciona un servidor"}
          </option>
          {serverOptions.map((server) => (
            <option key={server} value={server}>
              {server}
            </option>
          ))}
        </Input>
      </FormGroup>
    </div>
    <div className="w-100">
      <FormGroup>
        <Label className="text-muted text-uppercase small fw-semibold">
          Sucursal
        </Label>
        <Input
          type="select"
          multiple
          value={branchFilter}
          disabled={!serverFilter}
          onChange={(event) => {
            const target = event.target as unknown as HTMLSelectElement;
            const selected = Array.from(target.selectedOptions).map(
              (option) => option.value,
            );
            if (selected.includes("__all__")) {
              onBranchFilterChange([]);
              return;
            }
            onBranchFilterChange(selected.filter((value) => value !== "__all__"));
          }}
        >
          <option value="__all__">
            {branchOptions.length === 0 ? "Cargando..." : "Todas las sucursales"}
          </option>
          {branchOptions.map((branch) => (
            <option key={branch} value={branch}>
              {branch}
            </option>
          ))}
        </Input>
      </FormGroup>
    </div>
    <div className="w-100">
      <FormGroup className="w-100">
        <Label className="text-muted text-uppercase small fw-semibold">
          Tipo de control
        </Label>
        <Input
          type="select"
          value={controlMode}
          onChange={(event) =>
            onControlModeChange(event.target.value as "recetas" | "inventario")
          }
        >
          <option value="recetas">Control de artículos de recetas</option>
          <option value="inventario">Control por inventario sin receta</option>
        </Input>
      </FormGroup>
    </div>
    <div className="w-100">
      <FormGroup>
        <Label className="text-muted text-uppercase small fw-semibold">
          Diferencias
        </Label>
        <Input
          type="select"
          value={differenceFilter}
          onChange={(event) =>
            onDifferenceFilterChange(
              event.target.value as
                | "todas"
                | "con"
                | "sin"
                | "positivas"
                | "negativas",
            )
          }
        >
          <option value="todas">Con y sin diferencias</option>
          <option value="con">Solo con diferencias</option>
          <option value="positivas">Solo diferencias positivas</option>
          <option value="negativas">Solo diferencias negativas</option>
          <option value="sin">Solo sin diferencias</option>
        </Input>
      </FormGroup>
    </div>
    <div className="w-100">
      <FormGroup>
        <Label className="text-muted text-uppercase small fw-semibold">
          Modo de vista
        </Label>
        <Input
          type="select"
          value={metricMode}
          onChange={(event) =>
            onMetricModeChange(event.target.value as "cantidad" | "monto" | "ambos")
          }
        >
          <option value="cantidad">Cantidades</option>
          <option value="monto">Montos (PYG)</option>
          <option value="ambos">Cantidades + Montos</option>
        </Input>
      </FormGroup>
    </div>
  </div>
);

export default FiltersBar;
